import * as Api from '@utils/Api';
import { entityTypes } from 'constants/EntityTypes';

import { call, put, select } from 'redux-saga/effects';

import { types as reviewRuleTypes } from '@reducers/reviewRules';

export function* fetchReviewRules(action) {
  const { entityUUID, entityType } = action;
  let url = getUrl(entityType, entityUUID);
  if (entityType !== entityTypes.REPOSITORY) {
    url += `/${entityType}`;
  }
  const response = yield call(Api.get, url);
  if (!response.success) {
    yield put({
      type: reviewRuleTypes.REVIEW_RULES_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    // TODO: add pagination fetching support
    // If there is update in progress, then skip adding new data because there
    // will be another fetch after update is over.
    if (yield select((state) => state.reviewRules.isUpdating)) {
      return;
    }
    yield put({
      type: reviewRuleTypes.REVIEW_RULES_SUCCESS,
      entityUUID,
      entityType,
      rules: response.data.data,
    });
  }
}

// Using "params" instead of "action"
// because "action" is a parameter we want to send to server
export function* createReviewRule(params) {
  const { entityUUID, entityType, rule } = params;
  const { isBeforeRepository, field, comparator, action, value } = rule;
  // Make sure we have specific entity type
  const response = yield call(Api.post, getUrl(entityType, entityUUID), {
    before_repository: isBeforeRepository,
    field,
    comparator,
    action,
    value,
  });
  if (!response.success) {
    yield put({
      type: reviewRuleTypes.CREATE_REVIEW_RULES_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: reviewRuleTypes.CREATE_REVIEW_RULES_SUCCESS,
      entityType,
      rule: response.data,
    });
    yield fetchReviewRules({ entityUUID, entityType });
  }
}

function getTotalUpdates(state) {
  return state.reviewRules.totalUpdates;
}

// Using "params" instead of "action"
// because "action" is a parameter we want to send to server
export function* updateReviewRule(params) {
  const numTotalUpdates = yield select(getTotalUpdates);
  const { entityUUID, entityType, rule } = params;
  const {
    uuid: ruleUuid,
    isBeforeRepository,
    field,
    comparator,
    action,
    value,
    priority,
  } = rule;
  const response = yield call(
    Api.post,
    getUrl(entityType, entityUUID, ruleUuid),
    {
      before_repository: isBeforeRepository,
      field,
      comparator,
      action,
      value,
      priority,
    }
  );
  if (!response.success) {
    yield put({
      type: reviewRuleTypes.UPDATE_REVIEW_RULES_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: reviewRuleTypes.UPDATE_REVIEW_RULES_SUCCESS,
    });
    // Check to make sure there is not another update in progress
    const newNumTotalUpdates = yield select(getTotalUpdates);
    if (numTotalUpdates === newNumTotalUpdates) {
      yield fetchReviewRules({ entityUUID, entityType });
    }
  }
}

export function* deleteReviewRule(action) {
  const { entityUUID, entityType, rule } = action;
  const { uuid: ruleUuid } = rule;
  const response = yield call(
    Api.del,
    getUrl(entityType, entityUUID, ruleUuid)
  );
  if (!response.success) {
    yield put({
      type: reviewRuleTypes.DELETE_REVIEW_RULES_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: reviewRuleTypes.DELETE_REVIEW_RULES_SUCCESS,
      entityType,
      entityUUID,
      ruleDeleting: rule,
    });
    yield fetchReviewRules({ entityUUID, entityType });
  }
}

function getUrl(entityType, entityUUID, ruleUuid = null) {
  let url = null;
  if (entityType === entityTypes.REPOSITORY) {
    url = `projects/${entityUUID}/review-rules`;
  } else {
    url = `organizations/${entityUUID}/review-rules`;
  }
  url += ruleUuid ? `/${ruleUuid}` : '';
  return url;
}

export function* fetchReviewRulesSelections({ entityType, entityUuid }) {
  if (!entityType || !entityUuid) {
    throw new Error('Both entityType and entityUuid must be defined');
  }

  const url = getUrl(entityType, entityUuid) + '/selections';
  const response = yield call(Api.get, url);
  if (!response.success) {
    yield put({
      type: reviewRuleTypes.REVIEW_RULES_SELECTIONS_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: reviewRuleTypes.REVIEW_RULES_SELECTIONS_SUCCESS,
      selections: response.data,
      entityType,
      entityUuid,
    });
  }
}

export function* reorderReviewRules(action) {
  const { entityType, entityUUID, reviewRules, sourceIndex, destinationIndex } =
    action;
  const reorderedRules = Array.from(reviewRules);
  const [moved] = reorderedRules.splice(sourceIndex, 1);
  reorderedRules.splice(destinationIndex, 0, moved);
  yield put({
    type: reviewRuleTypes.REORDER_REVIEW_RULES_FINISH,
    reorderedRules,
    entityType,
    entityUUID,
  });
}
